import { Link } from "react-router-dom";
import {
  Search, Filter, ClipboardCheck, GraduationCap, FileText, Send, Stamp,
  Wallet, Home as HomeIcon, ArrowRight, Quote, Star, Compass, Users,
  ShieldCheck, CalendarCheck, Globe2, HeartHandshake, Handshake, Megaphone, Building2,
} from "lucide-react";
import { Button } from "../ui/button";
import { SectionHeading, Reveal, Eyebrow } from "../common";
import { DestinationCard, UniversityCard } from "../cards";
import {
  WHY_US, SERVICES, JOURNEY, TESTS, TESTIMONIALS, PARTNERS, SERVICE_PILLARS,
  DESTINATIONS, UNIVERSITIES,
} from "../../mock";

const PILLAR_ICONS = { Handshake, Users, Megaphone, Building2 };

const ICONS = {
  Search, Filter, ClipboardCheck, GraduationCap, FileText, Send,
  StampIcon: Stamp, Wallet, Home: HomeIcon,
};
const WHY_ICONS = [Compass, Star, Send, GraduationCap, Wallet, HeartHandshake];

export function WhyUs() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow="Why GDIH Elite"
          title="Amongst the most trusted study-abroad partners"
          subtitle="We don't just send you abroad — we build a roadmap that fits your ambitions, budget and future."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {WHY_US.map((w, i) => {
            const Icon = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <Reveal key={w.no} delay={i * 70}>
                <div className="group relative h-full bg-[#f7faf8] rounded-2xl p-7 border border-transparent hover:border-[#c9a227]/40 hover:bg-white hover-lift">
                  <div className="flex items-center justify-between">
                    <div className="w-14 h-14 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a] group-hover:bg-[#c9a227] group-hover:text-[#013d2b] transition-colors">
                      <Icon size={26} />
                    </div>
                    <span className="font-display text-4xl font-bold text-[#004030]/10 group-hover:text-[#c9a227]/25 transition-colors">{w.no}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#012a1e] mt-5">{w.title}</h3>
                  <p className="text-slate-500 mt-2.5 leading-relaxed text-[15px]">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ServicesSection() {
  return (
    <section className="section-pad bg-[#f4f8f6]">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow="Our Services"
          title="Begin your journey with GDIH Elite"
          subtitle="Nine expert-led steps that carry you from first idea to landing on campus."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Search;
            return (
              <Reveal key={s.no} delay={i * 50}>
                <div className="group h-full bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#004030]/20 hover-lift flex gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030] group-hover:bg-[#004030] group-hover:text-[#e0c05a] transition-colors">
                    <Icon size={22} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#c9a227] font-bold text-sm">{s.no}</span>
                      <h3 className="font-semibold text-[#012a1e] text-lg">{s.title}</h3>
                    </div>
                    <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function DestinationsPreview() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHeading
            center={false}
            eyebrow="Explore Opportunities"
            title="Educational destinations worldwide"
            subtitle="Each offering unique academic experiences, career pathways and cultural exposure."
          />
          <Link to="/destinations" className="shrink-0">
            <Button variant="outline" className="rounded-full border-[#004030] text-[#004030] hover:bg-[#004030] hover:text-white h-12 px-6 font-semibold">
              View all destinations <ArrowRight size={17} className="ml-1" />
            </Button>
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {DESTINATIONS.slice(0, 4).map((d, i) => (
            <Reveal key={d.slug} delay={i * 70}><DestinationCard d={d} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function JourneySection() {
  return (
    <section className="section-pad bg-[#004030] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#c9a227]/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6">
        <SectionHeading
          light
          eyebrow="How It Works"
          title="Your journey to studying abroad"
          subtitle="A dedicated, transparent process that keeps you informed and confident every step of the way."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {JOURNEY.map((j, i) => (
            <Reveal key={j.step} delay={i * 70}>
              <div className="relative h-full bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-[#c9a227] text-[#013d2b] font-display font-bold text-lg grid place-items-center">{j.step}</span>
                  <Eyebrow light>{j.tag}</Eyebrow>
                </div>
                <h3 className="font-display text-xl font-bold text-white mt-5">{j.title}</h3>
                <p className="text-white/65 mt-2 leading-relaxed text-[15px]">{j.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function UniversitiesPreview() {
  return (
    <section className="section-pad bg-[#f4f8f6]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHeading
            center={false}
            eyebrow="Our University Network"
            title="Partner universities across the globe"
            subtitle="From our featured partner UCSI University to the world's most prestigious institutions."
          />
          <Link to="/universities" className="shrink-0">
            <Button variant="outline" className="rounded-full border-[#004030] text-[#004030] hover:bg-[#004030] hover:text-white h-12 px-6 font-semibold">
              Browse universities <ArrowRight size={17} className="ml-1" />
            </Button>
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {UNIVERSITIES.slice(0, 3).map((u, i) => (
            <Reveal key={u.slug} delay={i * 70}><UniversityCard u={u} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestPrepPreview() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading
            center={false}
            eyebrow="Test Prep"
            title="Clear the exam before you clear the visa"
            subtitle="Small-batch coaching, real timed mocks and score targets set against the universities on your actual shortlist."
          />
          <ul className="mt-7 space-y-3">
            {["Expert-led speaking & writing feedback", "Full-length mocks scored to the real rubric", "Personalised score-target roadmap"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-slate-600">
                <ShieldCheck size={20} className="text-[#c9a227]" /> {t}
              </li>
            ))}
          </ul>
          <Link to="/test-prep">
            <Button className="mt-8 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 px-7 font-semibold">
              Explore Test Prep <ArrowRight size={17} className="ml-1" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {TESTS.map((t, i) => (
            <Reveal key={t.code} delay={i * 60}>
              <div className="rounded-2xl p-6 text-white hover-lift" style={{ backgroundColor: t.color }}>
                <GraduationCap size={26} className="text-[#e0c05a]" />
                <h3 className="font-display text-2xl font-bold mt-3">{t.name}</h3>
                <p className="text-white/70 text-sm mt-2 leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  if (!TESTIMONIALS.length) return null;
  return (
    <section className="section-pad bg-[#f4f8f6]">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow="Success Stories"
          title="Students succeeding globally"
          subtitle="There's nothing more rewarding than seeing our students achieve their dreams."
        />
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 90}>
              <div className="h-full bg-white rounded-2xl p-7 border border-slate-100 hover-lift relative">
                <Quote size={40} className="text-[#c9a227]/25 absolute top-5 right-5" />
                <div className="flex gap-1 text-[#c9a227]">
                  {[...Array(5)].map((_, s) => <Star key={s} size={16} className="fill-[#c9a227]" />)}
                </div>
                <p className="text-slate-600 mt-4 leading-relaxed italic">“{t.quote}”</p>
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-slate-100">
                  <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold text-[#012a1e]">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.course}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PartnersStrip() {
  if (!PARTNERS.length) return null;
  return (
    <section className="py-14 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
          Trusted by leading institutions worldwide
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8 mt-10">
          {PARTNERS.map((p) => (
            <img
              key={p.name}
              src={p.logo}
              alt={p.name}
              title={p.name}
              className="h-12 md:h-14 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhatWeDo() {
  return (
    <section className="section-pad bg-[#f4f8f6]">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow="What We Do"
          title="A full-service education group"
          subtitle="From placing students in world-class universities to representing institutions worldwide — nearly three decades of expertise under one roof."
        />
        <div className="grid sm:grid-cols-2 gap-6 mt-14">
          {SERVICE_PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[p.icon] || Handshake;
            return (
              <Reveal key={p.title} delay={i * 70}>
                <div className="group h-full bg-white rounded-2xl p-8 border border-slate-100 hover:border-[#c9a227]/40 hover-lift">
                  <div className="w-14 h-14 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a] group-hover:bg-[#c9a227] group-hover:text-[#013d2b] transition-colors">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#012a1e] mt-5">{p.title}</h3>
                  <p className="text-slate-500 mt-2.5 leading-relaxed text-[15px]">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function OSSDHighlight() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="rounded-3xl bg-[#004030] relative overflow-hidden grid lg:grid-cols-2 gap-8 items-center p-10 md:p-14">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#c9a227]/10 rounded-full blur-3xl" />
          <div className="relative">
            <Eyebrow light>Canadian High School, Online</Eyebrow>
            <h2 className="font-display font-bold text-white text-3xl md:text-[40px] leading-[1.12] mt-4">
              The OSSD Program
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed text-[15px]">
              Earn the Ontario Secondary School Diploma online and gain direct entry to English-speaking universities worldwide — often without needing IELTS or TOEFL.
            </p>
            <Link to="/ossd">
              <Button className="mt-7 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 px-7 font-bold">
                Explore OSSD <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            {[
              { k: "Grades 9–12", v: "Full Canadian curriculum" },
              { k: "30 Credits", v: "18 compulsory + 12 optional" },
              { k: "No IELTS/TOEFL", v: "For most universities" },
              { k: "100% Online", v: "Study from anywhere" },
            ].map((c) => (
              <div key={c.k} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="font-display font-bold text-[#e0c05a] text-xl">{c.k}</p>
                <p className="text-white/65 text-sm mt-1">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}